import { Button as MuiButton } from "@mui/material";
import { colorsFoundation } from "../tokens/colors-foundation.js";
import PropTypes from "prop-types";

/**
 * Button component with design-system variants: primary, secondary, tertiary, neutral
 * Based on Figma design system, wraps MUI Button for consistency
 *
 * Variants:
 * - primary: Blue background (main brand color) - maps to MUI 'secondary' color
 * - secondary: Gold background (accent brand color) - maps to MUI 'primary' color
 * - tertiary: Outlined with gold border - custom variant defined in theme
 * - neutral: Text-style button in neutral[700] for secondary/back/overview actions
 *
 * @param {object} props
 * @param {'primary'|'secondary'|'tertiary'|'neutral'} props.variant - Button style variant
 * @param {'medium'|'large'} props.size - Visual size (default medium)
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.type - Button type (button, submit, reset)
 * @param {function} props.onClick - Click handler
 * @param {React.ReactNode} props.children - Button content
 * @param {React.ReactNode} props.startIcon - Icon before text
 * @param {React.ReactNode} props.endIcon - Icon after text
 * @param {boolean} props.fullWidth - Full width button
 * @param {object} props.sx - Additional MUI sx styles
 */
export default function Button({
  variant = "primary",
  size = "medium",
  disabled = false,
  type = "button",
  onClick,
  children,
  startIcon,
  endIcon,
  fullWidth = false,
  sx = {},
  ...props
}) {
  // Map our design system variants to MUI variants and colors
  const getVariantProps = () => {
    switch (variant) {
      case "primary":
        // Primary = Blue contained button (maps to MUI secondary color)
        return {
          variant: "contained",
          color: "primary",
        };
      case "secondary":
        // Secondary = Gold contained button (maps to MUI primary color)
        return {
          variant: "contained",
          color: "secondary",
        };
      case "tertiary":
        // Tertiary = Gold outlined button (custom variant in theme)
        return {
          variant: "tertiary",
        };
      case "neutral":
        // Neutral = text-style button in neutral[700]
        return {
          variant: "text",
          sx: {
            color: colorsFoundation.neutral[700],
            "&:hover": {
              backgroundColor: colorsFoundation.neutral[50],
            },
          },
        };
      default:
        return {
          variant: "contained",
          color: "secondary",
        };
    }
  };

  const variantProps = getVariantProps();
  const { sx: variantSx, ...muiVariantProps } = variantProps;

  return (
    <MuiButton
      type={type}
      size={size}
      disabled={disabled}
      onClick={onClick}
      startIcon={startIcon}
      endIcon={endIcon}
      fullWidth={fullWidth}
      sx={{
        opacity: disabled ? 0.6 : 1,
        ...variantSx,
        ...sx,
      }}
      {...muiVariantProps}
      {...props}
    >
      {children}
    </MuiButton>
  );
}

Button.propTypes = {
  variant: PropTypes.oneOf(["primary", "secondary", "tertiary", "neutral"]),
  size: PropTypes.oneOf(["medium", "large", "small"]),
  disabled: PropTypes.bool,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  onClick: PropTypes.func,
  children: PropTypes.node,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  fullWidth: PropTypes.bool,
  sx: PropTypes.object,
};
